// Catálogo único de óleos com ficha para a aba "Protocolos de Óleos Essenciais".
// Fontes: PROTOCOLOS_OLEOS (adulto), PROTOCOLOS_KIDS (Touch + mixes infantis)
// e OLEOS_COMPLEMENTARES (Outras Opções). Nenhum texto é criado aqui:
// os campos são apenas lidos e organizados para exibição.

import { PROTOCOLOS_OLEOS, INSTRUCOES_ROTINA } from '@/utils/protocoloUso'
import { PROTOCOLOS_KIDS, FINALIDADE_CURTA } from '@/utils/protocoloKids'
import {
  FICHAS_SITUACOES,
  indicadoDaFicha,
  linhasDaSituacao,
  type FichaSituacoes,
} from '@/utils/fichasSituacoes'
import { OLEOS_COMPLEMENTARES } from '@/components/OutrasOpcoes'
import { CATALOGO_NOVOS, FICHAS_NOVAS } from '@/utils/fichasNovosOleos'
import { CATALOGO_ETAPA3, FICHAS_ETAPA3 } from '@/utils/fichasEtapa3'


export interface CampoProtocolo {
  rotulo: string
  texto: string
  // Rótulos que sempre aparecem sozinhos na linha, com os itens abaixo
  // (mesmo quando há um único item, como "Quando usar" com só "Manhã").
  bloco?: boolean
}

export type PeriodoRotina = 'manha' | 'dia' | 'noite' | 'conforme'

export interface PeriodoOleo {
  periodo: PeriodoRotina
  texto: string
}

export interface OleoProtocolo {
  id: string
  // Título usado no protocolo gerado e no texto copiado:
  // "Hortelã-Pimenta (Peppermint)" ou o nome atual quando o inglês já
  // aparece entre parênteses / é igual ao português.
  nome: string
  // Nome exibido na busca e na lista de selecionados:
  // "Peppermint - Hortelã-Pimenta".
  lista: string
  busca: string
  etiqueta?: 'Crianças'
  campos: CampoProtocolo[]
  // Seção da rotina diária: os 7 mixes infantis ficam em "Crianças";
  // adultos, Touch e complementares em "Adultos".
  secao: 'Adultos' | 'Crianças'
  // Período de uso lido da própria ficha (nunca inventado aqui).
  periodos: PeriodoOleo[]
}

// Remove o trecho entre parênteses do nome exibido
// ("Mix para Aterramento (Balance)" → "Mix para Aterramento").
function nomeSemParenteses(nome: string): string {
  return nome
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

// Compara dois nomes ignorando acentos e maiúsculas
function mesmoNome(a: string, b: string): boolean {
  const limpar = (texto: string) =>
    texto.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
  return limpar(a) === limpar(b)
}

// "Peppermint - Hortelã-Pimenta" (inglês primeiro, como na calculadora).
// Quando português e inglês são o mesmo nome, não repete.
function nomeLista(nome: string, ingles: string): string {
  const portugues = nomeSemParenteses(nome)
  if (mesmoNome(portugues, ingles)) return portugues
  return `${ingles} - ${portugues}`
}

// "Hortelã-Pimenta (Peppermint)"; não repete quando o inglês já está entre
// parênteses ou quando português e inglês são o mesmo nome (Vetiver, Copaíba).
function nomeTitulo(nome: string, ingles: string): string {
  if (/\([^)]*\)/.test(nome)) return nome
  const portugues = nomeSemParenteses(nome)
  if (mesmoNome(portugues, ingles)) return portugues
  return `${portugues} (${ingles})`
}

// ─── Períodos para a rotina diária ───────────────────────────────────────────

// Adultos: a mesma rotina manhã/dia/noite usada pela Rotina Diária do diagnóstico
function periodosAdulto(chave: string): PeriodoOleo[] {
  const rotina = INSTRUCOES_ROTINA[chave]
  const lista: PeriodoOleo[] = []
  if (!rotina) return lista
  if (rotina.manha) lista.push({ periodo: 'manha', texto: rotina.manha })
  if (rotina.dia) lista.push({ periodo: 'dia', texto: rotina.dia })
  if (rotina.noite) lista.push({ periodo: 'noite', texto: rotina.noite })
  return lista
}

// Crianças e Touch: o período vem do título da rotina na ficha
// (MANHÃ, NOITE, ANTES DE DORMIR, CONFORME NECESSIDADE, APÓS REFEIÇÕES...)
function periodosKids(chave: string): PeriodoOleo[] {
  const ficha = PROTOCOLOS_KIDS[chave]
  const lista: PeriodoOleo[] = []
  ficha?.rotinas?.forEach((rotina) => {
    const titulo = rotina.titulo.toUpperCase()
    if (titulo.includes('MANHÃ')) lista.push({ periodo: 'manha', texto: rotina.texto })
    if (titulo.includes('NOITE') || titulo.includes('DORMIR')) {
      lista.push({ periodo: 'noite', texto: rotina.texto })
    }
    if (titulo.includes('CONFORME NECESSIDADE') || titulo.includes('REFEI')) {
      lista.push({ periodo: 'conforme', texto: rotina.texto })
    }
  })
  return lista
}

// Complementares: as linhas "Manhã:", "Dia:" e "Noite:" de quandoUsar.
// Linhas sem período (ex.: "Difusor: 3 a 4 gotas") são ignoradas.
function periodosComplementar(quandoUsar?: string[]): PeriodoOleo[] {
  const lista: PeriodoOleo[] = []
  ;(quandoUsar || []).forEach((linha) => {
    const achado = linha.match(/^\s*(Manhã|Dia|Noite)\s*:\s*(.+)$/i)
    if (!achado) return
    const rotulo = achado[1].toLowerCase()
    const periodo: PeriodoRotina =
      rotulo === 'manhã' ? 'manha' : rotulo === 'dia' ? 'dia' : 'noite'
    lista.push({ periodo, texto: achado[2].trim() })
  })
  return lista
}

// Remove a palavra "Ingerir:" quando ela repete o sentido do rótulo do campo,
// para o texto começar direto em "pingue 1 gota em uma colher...".
// É só apresentação: nenhuma ficha é alterada.
function semIngerirDuplicado(texto: string): string {
  return texto
    .replace(/^\s*ingerir:\s*/i, '')
    .replace(/(Interno(?:\s+óleo)?:)\s*ingerir:\s*/gi, '$1 ')
    .trim()
}

// ─── Adultos ─────────────────────────────────────────────────────────────────

interface ItemAdulto {
  id: string
  nome: string
  chave: string
  // Nome em inglês exibido (mesmas grafias do catálogo da calculadora)
  ingles: string
  // Variações extras usadas somente para a busca
  apelidos?: string
  atencaoExtra?: string
}

const ADULTOS: ItemAdulto[] = [
  { id: 'adulto-lavanda', nome: 'Lavanda', chave: 'Lavanda', ingles: 'Lavender', apelidos: 'lavender' },
  { id: 'adulto-hortela', nome: 'Hortelã-Pimenta', chave: 'Hortelã-Pimenta', ingles: 'Peppermint', apelidos: 'peppermint' },
  { id: 'adulto-limao', nome: 'Limão Siciliano', chave: 'Limão Siciliano', ingles: 'Lemon', apelidos: 'lemon' },
  { id: 'adulto-olibano', nome: 'Olíbano', chave: 'Olíbano', ingles: 'Frankincense', apelidos: 'frankincense' },
  { id: 'adulto-copaiba', nome: 'Copaíba', chave: 'Copaíba', ingles: 'Copaiba', apelidos: 'copaiba' },
  { id: 'adulto-tangerina', nome: 'Tangerina', chave: 'Tangerina', ingles: 'Tangerine', apelidos: 'tangerine' },
  { id: 'adulto-melaleuca', nome: 'Melaleuca', chave: 'Melaleuca', ingles: 'Tea Tree', apelidos: 'tea tree melaleuca' },
  { id: 'adulto-balance', nome: 'Mix para Aterramento (Balance)', chave: 'Mix para Estabilidade', ingles: 'Balance', apelidos: 'balance' },
  { id: 'adulto-breathe', nome: 'Mix Respiratório (Breathe)', chave: 'Mix Respiratório', ingles: 'Breathe', apelidos: 'breathe air' },
  { id: 'adulto-deepblue', nome: 'Mix para Tensão e Dores (Deep Blue)', chave: 'Mix para Tensão e Dores', ingles: 'Deep Blue', apelidos: 'deep blue deepblue' },
  {
    id: 'adulto-onguard',
    nome: 'Mix de Proteção (On Guard)',
    chave: 'Mix Imunidade',
    ingles: 'On Guard',
    apelidos: 'on guard onguard',
    atencaoExtra:
      'Não inalar puro nas mãos — contém canela e pode irritar as narinas. Aplicar diluído e em áreas longe do sol.',
  },
  { id: 'adulto-zengest', nome: 'Mix Digestivo (Zengest)', chave: 'Mix Digestivo', ingles: 'Zengest', apelidos: 'zengest digestzen' },
  { id: 'adulto-metapwr', nome: 'Mix Metabólico (Meta PWR)', chave: 'Mix Metabólico', ingles: 'Meta PWR', apelidos: 'meta pwr metapwr' },
  { id: 'adulto-clarycalm', nome: 'Mix Mensal Feminino (ClaryCalm)', chave: 'Mix Mensal Feminino', ingles: 'ClaryCalm', apelidos: 'clarycalm clary calm' },
]

// Ficha por situação (Aromático / Tópico / Interno). É só apresentação:
// as fichas de origem continuam intactas.
function camposPorSituacao(ficha: FichaSituacoes): CampoProtocolo[] {
  const campos: CampoProtocolo[] = []
  campos.push({ rotulo: 'Indicado', texto: indicadoDaFicha(ficha) })
  if (ficha.comoAplicar) {
    campos.push({ rotulo: 'Como aplicar', texto: comPontoFinal(ficha.comoAplicar) })
  }
  ficha.situacoes.forEach((situacao) => {
    const linhas = linhasDaSituacao(ficha, situacao)
    if (linhas.length === 0) return
    campos.push({
      rotulo: situacao.situacao,
      texto: linhas.map(comPontoFinal).join('\n'),
      bloco: true,
    })
  })
  if (ficha.atencao) {
    campos.push({ rotulo: 'Atenção', texto: comPontoFinal(ficha.atencao) })
  }
  return campos
}

function camposAdulto(item: ItemAdulto): CampoProtocolo[] {
  const situacoes = FICHAS_SITUACOES[item.chave]
  if (situacoes) return camposPorSituacao(situacoes)

  const ficha = PROTOCOLOS_OLEOS[item.chave]
  if (!ficha) return []
  const campos: CampoProtocolo[] = []

  if (ficha.indicado) campos.push({ rotulo: 'Indicado', texto: ficha.indicado })
  if (ficha.aromatico) campos.push({ rotulo: 'Aromático', texto: ficha.aromatico })
  if (ficha.topico) campos.push({ rotulo: 'Tópico', texto: ficha.topico })
  if (ficha.topicoGordura) campos.push({ rotulo: 'Tópico para gordura localizada', texto: ficha.topicoGordura })
  if (ficha.topicoHormonal) campos.push({ rotulo: 'Tópico para equilíbrio hormonal', texto: ficha.topicoHormonal })
  if (ficha.topicoOndas) campos.push({ rotulo: 'Tópico para ondas de calor', texto: ficha.topicoOndas })
  if (ficha.interno) {
    campos.push({ rotulo: 'Interno', texto: semIngerirDuplicado(ficha.interno) })
  }
  if (ficha.usoDireto) campos.push({ rotulo: 'Uso direto', texto: ficha.usoDireto })

  // Rotina manhã / dia / noite mapeada como "Quando usar"
  const rotina = INSTRUCOES_ROTINA[item.chave]
  if (rotina) {
    const linhas: string[] = []
    if (rotina.manha) linhas.push(`Manhã: ${semIngerirDuplicado(rotina.manha)}`)
    if (rotina.dia) linhas.push(`Dia: ${semIngerirDuplicado(rotina.dia)}`)
    if (rotina.noite) linhas.push(`Noite: ${semIngerirDuplicado(rotina.noite)}`)
    if (linhas.length > 0) {
      campos.push({ rotulo: 'Quando usar', texto: linhas.join('\n'), bloco: true })
    }
  }

  // Atenção só aparece quando a ficha tem alerta cadastrado
  const partesAtencao: string[] = []
  if (ficha.atencao) partesAtencao.push(ficha.atencao)
  if (item.atencaoExtra) partesAtencao.push(item.atencaoExtra)
  if (partesAtencao.length > 0) {
    campos.push({ rotulo: 'Atenção', texto: partesAtencao.join(' ') })
  }

  return campos
}

// ─── Crianças (mixes) e linha Touch ──────────────────────────────────────────

interface ItemKids {
  chave: string
  nome: string
  // Nome em inglês exibido (mesmas grafias do catálogo da calculadora)
  ingles: string
  // Variações extras usadas somente para a busca
  apelidos?: string
  etiqueta?: 'Crianças'
  // Nome exibido na busca/lista quando difere do padrão "Inglês - Português"
  lista?: string
}

const MIXES_KIDS: ItemKids[] = [
  { chave: 'CALMER', nome: 'Mix para Serenidade (Calmer)', ingles: 'Calmer', apelidos: 'calmer', etiqueta: 'Crianças' },
  { chave: 'THINKER', nome: 'Mix para Foco (Thinker)', ingles: 'Thinker', apelidos: 'thinker', etiqueta: 'Crianças' },
  { chave: 'BRAVE', nome: 'Mix para Coragem (Brave)', ingles: 'Brave', apelidos: 'brave', etiqueta: 'Crianças' },
  { chave: 'STEADY', nome: 'Mix para Equilíbrio (Steady)', ingles: 'Steady', apelidos: 'steady', etiqueta: 'Crianças' },
  { chave: 'STRONGER', nome: 'Mix Protetor (Stronger)', ingles: 'Stronger', apelidos: 'stronger', etiqueta: 'Crianças' },
  { chave: 'TAMER', nome: 'Mix Digestivo (Tamer)', ingles: 'Tamer', apelidos: 'tamer', etiqueta: 'Crianças' },
  { chave: 'RESCUER', nome: 'Mix para Conforto (Rescuer)', ingles: 'Rescuer', apelidos: 'rescuer', etiqueta: 'Crianças' },
]

const TOUCH: ItemKids[] = [
  { chave: 'LAVANDA_TOUCH', nome: 'Lavanda Touch', ingles: 'Lavender Touch', apelidos: 'lavender touch' },
  { chave: 'VETIVER_TOUCH', nome: 'Vetiver Touch', ingles: 'Vetiver Touch', apelidos: 'vetiver touch' },
  { chave: 'BREATHE_TOUCH', nome: 'Breathe Touch (Mix Respiratório)', ingles: 'Breathe Touch', apelidos: 'breathe touch air touch mix respiratorio', lista: 'Breathe Touch - Mix Respiratório' },
  { chave: 'ON_GUARD_TOUCH', nome: 'On Guard Touch (Mix de Proteção)', ingles: 'On Guard Touch', apelidos: 'on guard touch onguard touch mix de protecao', lista: 'On Guard Touch - Mix de Proteção' },
  { chave: 'ADAPTIV_TOUCH', nome: 'Adaptiv Touch (Mix de Adaptabilidade)', ingles: 'Adaptiv Touch', apelidos: 'adaptiv touch mix de adaptabilidade', lista: 'Adaptiv Touch - Mix de Adaptabilidade' },
  { chave: 'HORTELA_TOUCH', nome: 'Hortelã Touch', ingles: 'Peppermint Touch', apelidos: 'peppermint touch' },
  { chave: 'ZENGEST_TOUCH', nome: 'Zengest Touch (Mix Digestivo)', ingles: 'Zengest Touch', apelidos: 'zengest touch digestzen touch mix digestivo', lista: 'Zengest Touch - Mix Digestivo' },
]

function camposKids(item: ItemKids): CampoProtocolo[] {
  const ficha = PROTOCOLOS_KIDS[item.chave]
  if (!ficha) return []
  const campos: CampoProtocolo[] = []

  // Indicado: o texto curto de finalidade da própria ficha, só com ponto final
  const finalidade = FINALIDADE_CURTA[item.chave]
  if (finalidade) campos.push({ rotulo: 'Indicado', texto: comPontoFinal(finalidade) })

  if (ficha.locaisPadrao?.length) {
    campos.push({ rotulo: 'Locais de aplicação', texto: ficha.locaisPadrao.join(', ') })
  }
  ficha.rotinas?.forEach((rotina) => {
    campos.push({ rotulo: `Rotina — ${rotina.titulo}`, texto: rotina.texto })
  })
  if (ficha.frequencia) campos.push({ rotulo: 'Frequência', texto: ficha.frequencia })

  // Atenção só aparece quando a ficha tem alerta cadastrado
  if (ficha.evitarLuzSolar) {
    campos.push({ rotulo: 'Atenção', texto: 'Evitar exposição ao sol após a aplicação.' })
  }

  return campos
}

// ─── Complementares (Outras Opções) ──────────────────────────────────────────

// Garante ponto final em cada item, sem duplicar pontuação existente
function comPontoFinal(texto: string): string {
  const limpo = texto.trim()
  if (!limpo) return limpo
  return /[.!?]$/.test(limpo) ? limpo : `${limpo}.`
}

// Chave de comparação para deduplicar alertas escritos de formas ligeiramente
// diferentes (pontuação, caixa, espaços)
function chaveAlerta(texto: string): string {
  return normalizarBusca(texto).replace(/[.!?;:,\s-]+/g, ' ').trim()
}

// Quebra um item em frases, para comparar alertas escritos em linhas separadas
// e também agrupados em uma única frase longa (caso do Mix Alegria)
function dividirEmFrases(texto: string): string[] {
  return texto
    .split(/(?<=[.!?])\s+/)
    .map((parte) => parte.trim())
    .filter(Boolean)
}

// Junta alertas + avisos removendo repetidos e mantendo a versão mais completa
function juntarAlertasUnicos(...listas: (string[] | undefined)[]): string[] {
  const porChave = new Map<string, string>()
  listas
    .flatMap((lista) => lista || [])
    .flatMap((item) => dividirEmFrases(item.trim()))
    .filter(Boolean)
    .forEach((frase) => {
      const chave = chaveAlerta(frase)
      if (!chave) return
      const existente = porChave.get(chave)
      // Mantém o texto mais longo (normalmente o que já traz a pontuação correta)
      if (!existente || frase.length > existente.length) porChave.set(chave, frase)
    })
  return Array.from(porChave.values())
}

// Nomes em inglês (e variações) dos complementares, usados somente na busca
const APELIDOS_COMPLEMENTARES: Record<string, string> = {
  vetiver: 'vetiver',
  serenity: 'serenity',
  bergamota: 'bergamot',
  cedro: 'cedarwood cedar',
  adaptiv: 'adaptiv',
  ddr_prime: 'ddr prime ddrprime',
  salvia_esclareia: 'clary sage clarysage',
  geranio: 'geranium',
  elevation: 'elevation',
  intune: 'intune in tune',
  zendocrine: 'zendocrine',
}

function apelidosComplementar(id: string): string {
  return APELIDOS_COMPLEMENTARES[id.toLowerCase()] || ''
}

// Nome em inglês exibido na busca/lista dos complementares
// (mesmas grafias do catálogo da calculadora).
const COMPLEMENTARES_INGLES: Record<string, string> = {
  vetiver: 'Vetiver',
  serenity: 'Serenity',
  bergamota: 'Bergamot',
  cedro: 'Cedarwood',
  adaptiv: 'Adaptiv',
  ddr_prime: 'DDR Prime',
  salvia_esclareia: 'Clary Sage',
  geranio: 'Geranium',
  elevation: 'Elevation',
  intune: 'InTune',
  zendocrine: 'Zendocrine',
}

const COMPLEMENTARES = OLEOS_COMPLEMENTARES.map((oleo) => ({
  id: `complementar-${oleo.id}`,
  nome: oleo.nome,
  ingles: COMPLEMENTARES_INGLES[oleo.id.toLowerCase()] || '',
  apelidos: apelidosComplementar(oleo.id),
  quandoUsar: oleo.protocolo.quandoUsar,
  campos: ((): CampoProtocolo[] => {
    const fichaSituacoes = FICHAS_SITUACOES[oleo.id.toLowerCase()]
    if (fichaSituacoes) return camposPorSituacao(fichaSituacoes)

    const campos: CampoProtocolo[] = []

    // Indicado: somente a lista das situações (as frases explicativas ficam de fora)
    const situacoes = (oleo.prioridades || []).filter(
      (situacao) => !!oleo.indicacoes?.[situacao]
    )
    if (situacoes.length > 0) {
      campos.push({ rotulo: 'Indicado', texto: comPontoFinal(situacoes.join(', ')) })
    }

    const comoUsar = (oleo.protocolo.comoUsar || []).filter(Boolean)
    if (comoUsar.length > 0) {
      campos.push({
        rotulo: 'Como usar',
        texto: comoUsar.map((linha) => comPontoFinal(semIngerirDuplicado(linha))).join('\n'),
        bloco: true,
      })
    }

    const quandoUsar = (oleo.protocolo.quandoUsar || []).filter(Boolean)
    if (quandoUsar.length > 0) {
      campos.push({
        rotulo: 'Quando usar',
        texto: quandoUsar.map(comPontoFinal).join('\n'),
        bloco: true,
      })
    }

    // Atenção só aparece quando a ficha tem alerta cadastrado
    const unicos = juntarAlertasUnicos(oleo.alertas, oleo.protocolo.avisos)
    if (unicos.length > 0) {
      campos.push({
        rotulo: 'Atenção',
        texto: unicos.map(comPontoFinal).join(' '),
      })
    }
    return campos
  })(),
}))

// ─── Catálogo final ──────────────────────────────────────────────────────────

// Junta o nome em português, o nome em inglês e as variações para a busca
function textoBusca(nome: string, ingles: string, apelidos?: string): string {
  return `${nome.toLowerCase()} ${ingles.toLowerCase()} ${(apelidos || '').toLowerCase()}`.trim()
}

export const OLEOS_PROTOCOLO: OleoProtocolo[] = [
  ...ADULTOS.map((item) => ({
    id: item.id,
    nome: nomeTitulo(item.nome, item.ingles),
    lista: nomeLista(item.nome, item.ingles),
    busca: textoBusca(item.nome, item.ingles, item.apelidos),
    campos: camposAdulto(item),
    secao: 'Adultos' as const,
    periodos: periodosAdulto(item.chave),
  })),
  ...TOUCH.map((item) => ({
    id: `touch-${item.chave.toLowerCase()}`,
    nome: nomeTitulo(item.nome, item.ingles),
    lista: item.lista || nomeLista(item.nome, item.ingles),
    busca: textoBusca(item.nome, item.ingles, item.apelidos),
    campos: camposKids(item),
    secao: 'Adultos' as const,
    periodos: periodosKids(item.chave),
  })),
  ...MIXES_KIDS.map((item) => ({
    id: `kids-${item.chave.toLowerCase()}`,
    nome: nomeTitulo(item.nome, item.ingles),
    lista: item.lista || nomeLista(item.nome, item.ingles),
    busca: textoBusca(item.nome, item.ingles, item.apelidos),
    etiqueta: item.etiqueta,
    campos: camposKids(item),
    secao: 'Crianças' as const,
    periodos: periodosKids(item.chave),
  })),
  ...COMPLEMENTARES.map((item) => ({
    id: item.id,
    nome: nomeTitulo(item.nome, item.ingles),
    lista: nomeLista(item.nome, item.ingles),
    busca: textoBusca(item.nome, item.ingles, item.apelidos),
    campos: item.campos,
    secao: 'Adultos' as const,
    periodos: periodosComplementar(item.quandoUsar),
  })),
  // Óleos novos (Etapa 2): existem apenas nesta aba
  ...CATALOGO_NOVOS.map((item) => ({
    id: item.id,
    nome: nomeTitulo(item.portugues, item.ingles),
    lista: nomeLista(item.portugues, item.ingles),
    busca: textoBusca(item.portugues, item.ingles),
    campos: camposPorSituacao(FICHAS_NOVAS[item.id]),
    secao: 'Adultos' as const,
    periodos: [],
  })),
  // Óleos novos (Etapa 3): existem apenas nesta aba
  ...CATALOGO_ETAPA3.map((item) => ({
    id: item.id,
    nome: nomeTitulo(item.portugues, item.ingles),
    lista: nomeLista(item.portugues, item.ingles),
    busca: textoBusca(item.portugues, item.ingles),
    campos: camposPorSituacao(FICHAS_ETAPA3[item.id]),
    secao: 'Adultos' as const,
    periodos: [],
  })),
].filter((oleo) => oleo.campos.length > 0)

export function normalizarBusca(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

export function buscarOleos(termo: string): OleoProtocolo[] {
  const alvo = normalizarBusca(termo)
  if (!alvo) return []
  return OLEOS_PROTOCOLO.filter((oleo) => normalizarBusca(oleo.busca).includes(alvo))
}

// Texto para WhatsApp: negrito com asteriscos, sem emojis, linha em branco entre óleos
export function gerarTextoProtocolo(
  oleos: OleoProtocolo[],
  nomeCliente?: string
): string {
  const blocos = oleos.map((oleo) => {
    const linhas = [`*${oleo.nome}*`]
    oleo.campos.forEach((campo) => {
      // Rótulos com vários itens (ex.: "Quando usar") ficam sozinhos na linha,
      // com cada item abaixo, um por linha — inclusive com um único item.
      if (campo.bloco || campo.texto.includes('\n')) {
        linhas.push(`*${campo.rotulo}:*`)
        campo.texto.split('\n').forEach((item) => {
          const limpo = item.trim()
          if (!limpo) return
          // Subitem sem recuo, com o sub-rótulo em negrito
          // (ex.: "*Aromático:* aplicar 1 gota...").
          const comSubRotulo = limpo.match(/^([^:]{1,60}):\s*(.+)$/)
          if (comSubRotulo) {
            linhas.push(`*${comSubRotulo[1].trim()}:* ${comSubRotulo[2].trim()}`)
          } else {
            linhas.push(limpo)
          }
        })
        return
      }
      linhas.push(`*${campo.rotulo}:* ${campo.texto}`)
    })
    return linhas.join('\n')
  })

  const nome = (nomeCliente || '').trim()
  const cabecalho = nome
    ? `*PROTOCOLO DE ÓLEOS ESSENCIAIS — ${nome}*`
    : '*PROTOCOLO DE ÓLEOS ESSENCIAIS*'

  return `${cabecalho}\n\n${blocos.join('\n\n')}`
}
